// src/components/Dashboard/AdminDashboard/AllUsers/AdminUnderageMessagingReview.js

import React, { useEffect, useState } from 'react';
import { apiFetch } from '../../../../apiFetch';
import { Link } from 'react-router-dom';
import './AdminUnderageMessagingReview.css';

const AdminUnderageMessagingReview = () => {
    const [requests, setRequests] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [roleFilter, setRoleFilter] = useState('all');
    const [searchTerm, setSearchTerm] = useState('');

    // 🔴 Decline flow
    const [declineId, setDeclineId] = useState(null);
    const [declineReason, setDeclineReason] = useState('');

    // ⚠️ DEV ONLY
    const IS_DEV = process.env.NODE_ENV !== 'production';

    /* ===============================
       🔄 FETCH REQUESTS
    =============================== */
    useEffect(() => {
        const fetchRequests = async () => {
            try {
                const res = await apiFetch('/api/admin/underage-message-requests');

                if (!res.ok) {
                    throw new Error('Failed to fetch underage messaging requests');
                }

                const data = await res.json();
                setRequests(data || []);
            } catch (err) {
                console.error(err);
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };

        fetchRequests();
    }, []);

    /* ===============================
       ✅ ADMIN APPROVE (ADMIN LAYER)
    =============================== */
    const handleApprove = async (id) => {
        try {
            const res = await apiFetch(
                `/api/admin/underage-message-request/${id}/status`,
                {
                    method: "PUT",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ status: "AdminApproved" }),
                }
            );

            if (!res.ok) throw new Error();

            setRequests((prev) =>
                prev.map((r) =>
                    r.id === id ? { ...r, status: 'AdminApproved' } : r
                )
            );
        } catch {
            alert('Failed to approve request.');
        }
    };

    /* ===============================
       ❌ ADMIN REJECT
    =============================== */
    const submitDecline = async () => {
        if (!declineReason.trim() || declineReason.trim().length < 5) {
            alert('Rejection reason must be at least 5 characters.');
            return;
        }

        try {
            const res = await apiFetch(
                `/api/admin/underage-message-request/${declineId}/status`,
                {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        status: 'Rejected',
                        rejectionReason: declineReason.trim(),
                        adminId: null,
                    }),
                }
            );

            if (!res.ok) throw new Error();

            setRequests((prev) =>
                prev.map((r) =>
                    r.id === declineId
                        ? {
                            ...r,
                            status: 'Rejected',
                            rejectionReason: declineReason.trim(),
                        }
                        : r
                )
            );

            setDeclineId(null);
            setDeclineReason('');
        } catch {
            alert('Failed to reject request.');
        }
    };

    /* ===============================
       👨‍👩‍👦 DEV: APPROVE AS PARENT
    =============================== */
    const handleApproveAsParent = async (id) => {
        if (!window.confirm('Approve this request as parent? (DEV ONLY)')) return;

        try {
            const res = await apiFetch(
                `/api/admin/underage-message-request/${id}/approve-parent`,
                { method: "POST" }
            );

            if (!res.ok) throw new Error();

            setRequests((prev) =>
                prev.map((r) =>
                    r.id === id ? { ...r, status: 'Approved' } : r
                )
            );
        } catch {
            alert('Failed to approve as parent.');
        }
    };

    /* ===============================
       ⚪ COMPLETE CONSENT (END APPROVED CYCLE)
    =============================== */
    const handleCompleteConsent = async (id) => {
        if (!window.confirm('End this consent and allow a new request?')) return;

        try {
            const res = await apiFetch(
                `/api/admin/underage-message-request/${id}/complete`,
                { method: 'POST' }
            );

            if (!res.ok) throw new Error();

            setRequests((prev) =>
                prev.map((r) =>
                    r.id === id ? { ...r, status: 'Completed' } : r
                )
            );
        } catch {
            alert('Failed to complete consent.');
        }
    };

    /* ===============================
       🔍 FILTERING
    =============================== */
    const normalizedSearch = searchTerm.toLowerCase().trim();

    const filteredRequests = requests
        .filter((r) =>
            roleFilter === 'all' ? true : r.senderRole === roleFilter
        )
        .filter((r) =>
            !normalizedSearch ||
            [
                r.playerFullName,
                r.playerUsername,
                r.senderUsername,
                r.intent,
            ].some(
                (f) => f && f.toLowerCase().includes(normalizedSearch)
            )
        );

    /* ===============================
       🧱 RENDER
    =============================== */
    return (
        <div className="admin-underage-review">
            <h2>Underage Messaging Requests</h2>

            <input
                className="verification-search-input"
                placeholder="Search by player, sender, username, or intent..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
            />

            <div className="role-filter-row">
                <strong>Filter by role:</strong>
                <select
                    value={roleFilter}
                    onChange={(e) => setRoleFilter(e.target.value)}
                >
                    <option value="all">All</option>
                    <option value="agent">Agents</option>
                    <option value="scout">Scouts</option>
                    <option value="club">Clubs</option>
                </select>
            </div>

            {loading ? (
                <p>Loading…</p>
            ) : error ? (
                <p style={{ color: 'red' }}>❌ {error}</p>
            ) : (
                <div className="underage-request-list">
                    {filteredRequests.map((req) => (
                        <div key={req.id} className="underage-request-card">
                            <p>
                                <strong>Sender:</strong>{' '}
                                {req.senderRole?.toUpperCase()}{' '}
                                {req.senderUsername && req.senderProfileSlug && (
                                    <Link
                                        to={`/${req.senderRole}-profile/${req.senderProfileSlug}`}
                                        className="profile-link"
                                    >
                                        ({req.senderUsername})
                                    </Link>
                                )}
                            </p>

                            <p>
                                <strong>Player:</strong>{' '}
                                {req.playerFullName}{' '}
                                {req.playerUsername && req.playerProfileSlug && (
                                    <Link
                                        to={`/player-profile/${req.playerProfileSlug}`}
                                        className="profile-link"
                                    >
                                        ({req.playerUsername})
                                    </Link>
                                )}
                            </p>

                            <p><strong>Intent:</strong> {req.intent}</p>
                            <p><strong>Message:</strong> {req.message}</p>

                            <p>
                                <strong>Status:</strong>{' '}
                                <span className={`status-text status-${req.status?.toLowerCase()}`}>
                                    {req.status}
                                </span>
                            </p>

                            {req.rejectionReason && (
                                <p style={{ color: '#ffb3b3' }}>
                                    <strong>Rejection Reason:</strong> {req.rejectionReason}
                                </p>
                            )}

                            {/* 🔴 ADMIN ACTIONS */}
                            {req.status === 'Pending' && (
                                <div className="underage-actions">
                                    <button
                                        className="approve-underage-btn"
                                        onClick={() => handleApprove(req.id)}
                                    >
                                        Approve
                                    </button>

                                    <button
                                        className="decline-underage-btn"
                                        onClick={() => {
                                            setDeclineId(req.id);
                                            setDeclineReason('');
                                        }}
                                    >
                                        Decline
                                    </button>
                                </div>
                            )}

                            {/* 👨‍👩‍👦 DEV ONLY – PARENT APPROVAL */}
                            {IS_DEV && req.status === 'AdminApproved' && (
                                <div className="underage-actions">
                                    <button
                                        className="approve-underage-btn"
                                        onClick={() => handleApproveAsParent(req.id)}
                                    >
                                        Approve as Parent (DEV)
                                    </button>
                                </div>
                            )}

                            {/* ⚪ COMPLETE CONSENT */}
                            {req.status === 'Approved' && (
                                <div className="underage-actions">
                                    <button
                                        className="complete-consent-btn"
                                        onClick={() => handleCompleteConsent(req.id)}
                                    >
                                        Complete Consent
                                    </button>
                                </div>
                            )}

                            {declineId === req.id && (
                                <div className="decline-reason-box">
                                    <textarea
                                        placeholder="Enter rejection reason..."
                                        value={declineReason}
                                        onChange={(e) => setDeclineReason(e.target.value)}
                                    />
                                    <button onClick={submitDecline}>
                                        Submit Rejection
                                    </button>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default AdminUnderageMessagingReview;






